import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../theme';
import { TOAST_CONFIG, TOAST_TRANSITION, TOAST_WIDTH_VAR, getToastStyles, XIcon, } from './Toast.styles';
const ToastComponent = ({ notification, onDismiss, style }) => {
    const theme = useTheme();
    const [isHovered, setIsHovered] = useState(false);
    const { id, type, title, message, duration, dismissible = true, action } = notification;
    const toastStyles = getToastStyles(theme);
    const typeStyle = toastStyles[type];
    const IconComponent = typeStyle.icon;
    // Auto-dismiss timer that pauses on hover
    useEffect(() => {
        if (duration && dismissible && !isHovered) {
            const timer = setTimeout(() => {
                onDismiss(id);
            }, duration);
            return () => clearTimeout(timer);
        }
    }, [duration, dismissible, isHovered, id, onDismiss]);
    const handleDismiss = useCallback(() => {
        onDismiss(id);
    }, [id, onDismiss]);
    const handleActionClick = useCallback(() => {
        if (action?.handler) {
            action.handler();
        }
    }, [action]);
    // Use CSS min() for responsive width - avoids JS-based detection, SSR hydration issues, and FOUC.
    // The host's toast region may override it through `--toast-width` (WEB-1109: the app's ≥768
    // bottom-left column is a fixed 400px); an inline width would otherwise beat any stylesheet.
    // The fallback's min() is the only cap, so no `maxWidth` overrides a host's value.
    const baseStyle = {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing.sm,
        minHeight: TOAST_CONFIG.minHeight,
        width: `var(${TOAST_WIDTH_VAR}, min(${TOAST_CONFIG.mobileWidth}, ${TOAST_CONFIG.maxWidth}px))`,
        padding: `${TOAST_CONFIG.padding.vertical}px ${TOAST_CONFIG.padding.horizontal}px`,
        borderRadius: TOAST_CONFIG.borderRadius,
        backgroundColor: typeStyle.background,
        border: `1px solid ${typeStyle.border}`,
        color: typeStyle.text,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        fontFamily: theme.typography.fontFamily,
        boxSizing: 'border-box',
        ...style,
    };
    const iconStyle = {
        flexShrink: 0,
        width: TOAST_CONFIG.iconSize,
        height: TOAST_CONFIG.iconSize,
        marginTop: 2,
    };
    const contentStyle = {
        flex: 1,
        minWidth: 0,
    };
    const titleStyle = {
        fontSize: theme.typography.fontSize.small,
        fontWeight: theme.typography.fontWeight.semibold,
        lineHeight: 1.4,
        margin: 0,
    };
    const messageStyle = {
        fontSize: theme.typography.fontSize.caption,
        fontWeight: theme.typography.fontWeight.normal,
        lineHeight: 1.4,
        marginTop: 4,
        // WEB-1100: no `opacity` — dimming 12px text blended the ink toward the
        // fill and dropped it under WCAG AA (4.39–4.43:1 on light success/info).
    };
    // Touch target: 44px minHeight for accessibility, width determined by content
    const actionButtonStyle = {
        display: 'inline-block',
        marginTop: 8,
        padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
        fontSize: theme.typography.fontSize.caption,
        fontWeight: theme.typography.fontWeight.medium,
        color: typeStyle.text,
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        borderRadius: 4,
        cursor: 'pointer',
        minHeight: TOAST_CONFIG.minHeight,
    };
    // Touch target: 44px minHeight for accessibility, width sized to icon
    const dismissButtonStyle = {
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: TOAST_CONFIG.closeButtonSize,
        height: TOAST_CONFIG.closeButtonSize,
        padding: 0,
        backgroundColor: 'transparent',
        border: 'none',
        color: typeStyle.text,
        cursor: 'pointer',
        opacity: 0.8,
        borderRadius: 4,
        minHeight: TOAST_CONFIG.minHeight,
    };
    const dismissButtonHoverStyle = {
        opacity: 1,
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
    };
    // No `layout` (WEB-1123): `ToastContainer`'s slot moves the card in real
    // layout — a FLIP here painted cards over each other (`toastStackSlot.ts`).
    return (_jsxs(motion.div, { initial: { opacity: 0, y: -20, scale: 0.95 }, animate: { opacity: 1, y: 0, scale: 1 }, exit: { opacity: 0, scale: 0.95, transition: { duration: 0.15 } }, transition: TOAST_TRANSITION, role: "alert", "aria-live": type === 'error' ? 'assertive' : 'polite', "aria-atomic": "true", style: baseStyle, onMouseEnter: () => setIsHovered(true), onMouseLeave: () => setIsHovered(false), children: [_jsx(IconComponent, { style: iconStyle, "aria-hidden": "true" }), _jsxs("div", { style: contentStyle, children: [_jsx("p", { style: titleStyle, children: title }), message && _jsx("p", { style: messageStyle, children: message }), action && (_jsx("button", { type: "button", style: actionButtonStyle, onClick: handleActionClick, onMouseEnter: (e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
                        }, onMouseLeave: (e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                        }, children: action.label }))] }), dismissible && (_jsx("button", { type: "button", style: dismissButtonStyle, onClick: handleDismiss, onMouseEnter: (e) => {
                    Object.assign(e.currentTarget.style, dismissButtonHoverStyle);
                }, onMouseLeave: (e) => {
                    e.currentTarget.style.opacity = '0.8';
                    e.currentTarget.style.backgroundColor = 'transparent';
                }, "aria-label": "Dismiss notification", children: _jsx(XIcon, { size: 16, "aria-hidden": "true" }) }))] }));
};
export const Toast = React.memo(ToastComponent);
Toast.displayName = 'Toast';
